/* eslint-disable import/extensions */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  HAS_NUMBER_REGEX,
  MOBILE_REGEX,
  CIF_REGEX,
  isValidNIE,
  isValidNIF,
  isValidPassport,
  isValidCIF,
  NO_NUMBERS_ERROR,
  REQUIRED_FIELD_ERROR,
  MIN_AGE_ERROR,
  MAX_AGE_ERROR,
  FIELD_INVALID,
} from '@masfront/common-b2c'
import { subYears } from 'date-fns'
import { date, object, string } from 'yup'

import { CustomerSegment } from '~/basket/model/common/segment.model'
// @TODO Move this to a common place
export function mapYupToFormikErrors(err: { inner: any[] }) {
  return err.inner
    .filter((i: { message: any }) => !!i.message)
    .reduce(
      (curr: any, next: { path: any; errors: any[] }) => ({
        ...curr,
        [next.path]: next.errors[0],
      }),
      {},
    )
}

function validateDocIdValue(value: string, isPostpaid: boolean): boolean {
  const postpaidValidation = isValidNIF(value) || isValidNIE(value)
  const prepaidValidation = postpaidValidation || isValidPassport(value)

  const validation = isPostpaid ? postpaidValidation : prepaidValidation

  return !!(value && !isValidCIF(value) && validation)
}

const customerInfoSchema = (isPostpaid: boolean) =>
  object().shape({
    customerSegment: string().label('segmento').required(REQUIRED_FIELD_ERROR),
    name: string()
      .label('nombre')
      .matches(HAS_NUMBER_REGEX, NO_NUMBERS_ERROR)
      .required(REQUIRED_FIELD_ERROR),
    nationality: string().label('país de origen').required(REQUIRED_FIELD_ERROR),
    gender: string()
      .label('género')
      .when('customerSegment', {
        is: !CustomerSegment.COMPANY,
        then: string().required(REQUIRED_FIELD_ERROR),
        otherwise: string(),
      }),
    firstName: string()
      .label('apellido')
      .matches(HAS_NUMBER_REGEX, NO_NUMBERS_ERROR)
      .required(REQUIRED_FIELD_ERROR),
    lastName: string()
      .label('segundo apellido')
      .when('docId', {
        is: (docId) => docId && isValidNIF(docId),
        then: string().matches(HAS_NUMBER_REGEX, NO_NUMBERS_ERROR).required(REQUIRED_FIELD_ERROR),
        otherwise: string().matches(HAS_NUMBER_REGEX, NO_NUMBERS_ERROR),
      }),
    birthdate: date()
      .label('campo fecha')
      .when('customerSegment', {
        is: CustomerSegment.COMPANY,
        then: date().nullable(),
        otherwise: date()
          .min(subYears(new Date(), 120), MIN_AGE_ERROR)
          .max(subYears(new Date(), 18), MAX_AGE_ERROR)
          .required(REQUIRED_FIELD_ERROR),
      }),
    contactPhone: string()
      .label('teléfono móvil')
      .matches(MOBILE_REGEX, FIELD_INVALID)
      .required(REQUIRED_FIELD_ERROR),
    docId: string()
      .trim()
      .label('Documento')
      .required(REQUIRED_FIELD_ERROR)
      .test('isNifOrNie', FIELD_INVALID, (value) => validateDocIdValue(value, isPostpaid)),
    companyName: string()
      .label('nombre de la empresa')
      .when('customerSegment', {
        is: CustomerSegment.COMPANY,
        then: string().required(REQUIRED_FIELD_ERROR),
        otherwise: string(),
      }),
    cif: string().when('customerSegment', {
      is: CustomerSegment.COMPANY,
      then: string().matches(CIF_REGEX, FIELD_INVALID).label('CIF').required(REQUIRED_FIELD_ERROR),
      otherwise: string(),
    }),
  })

export const companySchema = object().shape({
  cif: string().matches(CIF_REGEX, FIELD_INVALID).label('CIF').required(REQUIRED_FIELD_ERROR),
})

export function validateSchema(values: unknown, schema: ReturnType<typeof customerInfoSchema>) {
  return schema
    .validate(values, {
      abortEarly: false,
      strict: false,
    })
    .then(() => {
      return {}
    })
    .catch(mapYupToFormikErrors)
}

export async function validateDocId(
  value: string | number,
  name: string,
  type: string,
  isPostpaid: boolean,
  asyncFn: (value: string | number, name: string, type: string) => Promise<boolean>,
) {
  const schemaField: string = name.split('.').pop() || ''
  const schema = customerInfoSchema(isPostpaid)
  const syncErrors = await validateSchema({ [schemaField]: value }, schema)

  if (syncErrors[schemaField]) {
    return null
  }

  asyncFn(value, name, type)

  return null
}

export default customerInfoSchema
